import { useState, useMemo, useRef, useCallback } from "react";
import { useApp } from "../context/AppContext";

export default function WikiLinkView() {
  const { proj, activeId, getStore, apiFetch, flash, dark, t, card, inp, btnP, btnG, btnSm, stitle, muted } = useApp();
  if (!proj) return null;
  const store = getStore(activeId);
  const clusters = store.clusters || [];

  const [inputText, setInputText] = useState("");
  const [inputHtml, setInputHtml] = useState("");
  const [result, setResult] = useState(null);
  const [minLinks, setMinLinks] = useState(5);
  const [maxLinks, setMaxLinks] = useState(10);
  const [loading, setLoading] = useState(false);
  const [ngramData, setNgramData] = useState(null);
  const [ngramLoaded, setNgramLoaded] = useState(false);
  const resultRef = useRef(null);
  const editorRef = useRef(null);

  const getEditorText = () => editorRef.current?.innerText || "";
  const getEditorHtml = () => editorRef.current?.innerHTML || "";

  // Build keyword → URL map from clusters
  const keywordMap = useMemo(() => {
    const map = []; // {keyword, url, frequency}
    clusters.forEach(c => {
      if (!c.url) return;
      (c.keywords || []).forEach(k => {
        if (k.keyword && k.keyword.length >= 3) {
          map.push({ keyword: k.keyword.toLowerCase().trim(), original: k.keyword, url: c.url, frequency: k.frequency || 0 });
        }
      });
    });
    // Sort by keyword length desc (match longer phrases first), then by frequency
    map.sort((a, b) => b.keyword.split(" ").length - a.keyword.split(" ").length || b.frequency - a.frequency);
    return map;
  }, [clusters]);

  // Load n-grams
  const loadNgrams = async () => {
    if (ngramLoaded) return ngramData;
    try {
      const r = await apiFetch(`/api/projects/${activeId}/ngrams/saved`);
      const d = await r.json();
      if (d && d.data) {
        setNgramData(d.data);
        setNgramLoaded(true);
        return d.data;
      }
    } catch {}
    setNgramLoaded(true);
    return null;
  };

  const processText = async () => {
    const plainText = getEditorText();
    const htmlText = getEditorHtml();
    if (!plainText.trim()) return;
    setLoading(true);
    setInputText(plainText);
    setInputHtml(htmlText);

    // Load n-grams for additional matching
    const ngrams = await loadNgrams();

    // Build combined keyword pool: clusters + n-grams
    const pool = [...keywordMap];

    // Add n-gram bigrams/trigrams that match cluster URLs
    if (ngrams) {
      const urlSet = new Set(clusters.map(c => c.url));
      const clusterByUrl = {};
      clusters.forEach(c => { clusterByUrl[c.url] = c; });

      // For each n-gram, find best matching cluster URL
      const addNgrams = (grams) => {
        if (!Array.isArray(grams)) return;
        grams.forEach(g => {
          if (!g.gram || g.gram.length < 3) return;
          const gramLow = g.gram.toLowerCase();
          // Find cluster whose keywords contain this gram
          for (const c of clusters) {
            if (!c.url) continue;
            const kwMatch = (c.keywords || []).some(k =>
              k.keyword.toLowerCase().includes(gramLow) || gramLow.includes(k.keyword.toLowerCase())
            );
            if (kwMatch) {
              pool.push({ keyword: gramLow, original: g.gram, url: c.url, frequency: g.count || 0 });
              break;
            }
          }
        });
      };
      addNgrams(ngrams.bigrams);
      addNgrams(ngrams.trigrams);
    }

    // Deduplicate: keep highest frequency per keyword
    const uniquePool = {};
    pool.forEach(p => {
      if (!uniquePool[p.keyword] || uniquePool[p.keyword].frequency < p.frequency) {
        uniquePool[p.keyword] = p;
      }
    });
    const candidates = Object.values(uniquePool)
      .sort((a, b) => b.keyword.split(" ").length - a.keyword.split(" ").length || b.frequency - a.frequency);

    // Find matches in plain text (for position detection)
    const textLow = plainText.toLowerCase();
    const matches = [];
    const usedRanges = [];
    const usedUrls = new Set();

    for (const cand of candidates) {
      if (matches.length >= maxLinks) break;
      let searchFrom = 0;
      while (searchFrom < textLow.length) {
        const idx = textLow.indexOf(cand.keyword, searchFrom);
        if (idx === -1) break;
        const before = idx > 0 ? textLow[idx - 1] : " ";
        const after = idx + cand.keyword.length < textLow.length ? textLow[idx + cand.keyword.length] : " ";
        const isWord = /[\s,.;:!?()"\-—]/.test(before) || idx === 0;
        const isWordEnd = /[\s,.;:!?()"\-—]/.test(after) || (idx + cand.keyword.length) === textLow.length;
        if (isWord && isWordEnd) {
          const end = idx + cand.keyword.length;
          const overlaps = usedRanges.some(r => !(end <= r.start || idx >= r.end));
          if (!overlaps && !usedUrls.has(cand.url)) {
            matches.push({ start: idx, end, keyword: plainText.slice(idx, end), url: cand.url, frequency: cand.frequency });
            usedRanges.push({ start: idx, end });
            usedUrls.add(cand.url);
            break;
          }
        }
        searchFrom = idx + 1;
      }
    }

    matches.sort((a, b) => a.start - b.start);

    // Build result HTML from original HTML — insert <a> tags
    // We work with the HTML, replacing keyword text nodes with linked versions
    // Insert links into HTML — use DOM parser for safety
    const tmpDiv = document.createElement("div");
    tmpDiv.innerHTML = htmlText;

    const usedUrlsHtml = new Set();
    const walker = document.createTreeWalker(tmpDiv, NodeFilter.SHOW_TEXT, null);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    for (const m of matches) {
      if (usedUrlsHtml.has(m.url)) continue;
      const kwLow = m.keyword.toLowerCase();
      for (const node of textNodes) {
        if (node.parentElement?.tagName === "A") continue; // skip already linked
        const idx = node.textContent.toLowerCase().indexOf(kwLow);
        if (idx === -1) continue;
        // Split text node and insert link
        const before = node.textContent.slice(0, idx);
        const matched = node.textContent.slice(idx, idx + m.keyword.length);
        const after = node.textContent.slice(idx + m.keyword.length);
        const link = document.createElement("a");
        link.href = m.url;
        link.style.cssText = "color:#6366f1;text-decoration:underline";
        link.textContent = matched;
        const frag = document.createDocumentFragment();
        if (before) frag.appendChild(document.createTextNode(before));
        frag.appendChild(link);
        if (after) frag.appendChild(document.createTextNode(after));
        node.parentNode.replaceChild(frag, node);
        usedUrlsHtml.add(m.url);
        break;
      }
    }
    const resultHtml = tmpDiv.innerHTML;

    setResult({
      html: resultHtml,
      matches,
      totalChars: plainText.length,
      linkedCount: matches.length,
      originalHtml: htmlText,
    });
    setLoading(false);
  };

  const copyHtml = () => {
    if (!result) return;
    navigator.clipboard.writeText(result.html);
    flash("HTML з посиланнями скопійовано");
  };

  const copyCleanHtml = () => {
    if (!result) return;
    // Remove inline styles from links, keep clean <a href>
    const clean = result.html.replace(/ style="[^"]*"/g, "");
    navigator.clipboard.writeText(clean);
    flash("Чистий HTML скопійовано");
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>🔗 Вікі-лінковка</h2>
        <span style={{ fontSize: 12, color: t.textMuted }}>
          {clusters.length} кластерів · {keywordMap.length} ключів
        </span>
      </div>

      {clusters.length === 0 && (
        <div style={{ ...card, textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>📭</div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Немає семантики</div>
          <div style={{ fontSize: 12, color: t.textMuted }}>Завантажте семантику (CSV) щоб використовувати вікі-лінковку</div>
        </div>
      )}

      {clusters.length > 0 && (
        <>
          {/* Input */}
          <div style={{ ...card, padding: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <p style={stitle}>Текст для лінковки</p>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <label style={{ fontSize: 11, color: t.textMuted }}>Від</label>
                <input type="number" value={minLinks} onChange={e => setMinLinks(Math.max(1, +e.target.value))} min={1} max={20}
                  style={{ ...inp, width: 50, textAlign: "center", padding: "4px 6px", fontSize: 12 }} />
                <label style={{ fontSize: 11, color: t.textMuted }}>до</label>
                <input type="number" value={maxLinks} onChange={e => setMaxLinks(Math.max(minLinks, +e.target.value))} min={1} max={30}
                  style={{ ...inp, width: 50, textAlign: "center", padding: "4px 6px", fontSize: 12 }} />
                <label style={{ fontSize: 11, color: t.textMuted }}>посилань</label>
              </div>
            </div>
            <div
              ref={editorRef}
              contentEditable
              onPaste={e => {
                // Allow rich paste — keep HTML formatting
                const html = e.clipboardData.getData("text/html");
                if (html) {
                  e.preventDefault();
                  const tmp = document.createElement("div");
                  tmp.innerHTML = html;
                  tmp.querySelectorAll("script, style, iframe, link, noscript, meta").forEach(el => el.remove());
                  // Remove classes/ids/styles but keep structure
                  const clean = (node) => {
                    for (const child of [...node.children]) {
                      const tag = child.tagName.toLowerCase();
                      const allowed = ["h1","h2","h3","h4","h5","p","br","strong","b","em","i","u","a","ul","ol","li","table","tr","td","th","thead","tbody","blockquote","img","figure","figcaption","span"];
                      if (!allowed.includes(tag)) {
                        while (child.firstChild) child.parentNode.insertBefore(child.firstChild, child);
                        child.remove();
                      } else {
                        const keepAttrs = ["href", "src", "alt"];
                        [...child.attributes].forEach(attr => { if (!keepAttrs.includes(attr.name)) child.removeAttribute(attr.name); });
                        clean(child);
                      }
                    }
                  };
                  clean(tmp);
                  document.execCommand("insertHTML", false, tmp.innerHTML);
                }
              }}
              style={{
                ...inp, minHeight: 200, maxHeight: 500, overflowY: "auto", resize: "vertical",
                fontSize: 14, lineHeight: 1.7, fontFamily: "inherit", padding: "12px 14px",
                outline: "none", whiteSpace: "pre-wrap",
              }}
              data-placeholder="Вставте текст з HTML розміткою (H2, H3, списки) або plain text — система збереже форматування та додасть внутрішні посилання..."
              suppressContentEditableWarning
            />
            <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
              <button onClick={processText} disabled={loading} style={{
                ...btnP, opacity: loading ? 0.5 : 1,
              }}>
                {loading ? "Обробка..." : "🔗 Лінкувати"}
              </button>
              <span style={{ fontSize: 11, color: t.textMuted }}>
                {editorRef.current?.innerText?.length || 0} символів
              </span>
            </div>
          </div>

          {/* Result */}
          {result && (
            <div style={{ ...card, padding: 16, marginTop: 0 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <p style={{ ...stitle, margin: 0 }}>Результат</p>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 8,
                    background: result.linkedCount >= minLinks ? (dark ? "rgba(16,185,129,0.15)" : "#ecfdf5") : (dark ? "rgba(245,158,11,0.1)" : "#fffbeb"),
                    color: result.linkedCount >= minLinks ? "#10b981" : "#f59e0b",
                  }}>
                    {result.linkedCount} посилань
                  </span>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={copyHtml} style={btnSm}>📋 HTML</button>
                  <button onClick={copyCleanHtml} style={btnSm}>📋 Чистий HTML</button>
                </div>
              </div>

              {/* Preview */}
              <div
                ref={resultRef}
                style={{
                  padding: 16, borderRadius: 10, fontSize: 13, lineHeight: 1.8,
                  background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
                  border: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}`,
                  maxHeight: 500, overflowY: "auto",
                }}
                dangerouslySetInnerHTML={{ __html: `<style>a{color:#6366f1;text-decoration:none;background:${dark ? "rgba(99,102,241,0.12)" : "rgba(99,102,241,0.08)"};padding:1px 4px;border-radius:4px;border-bottom:2px solid ${dark ? "#6366f1" : "#a5b4fc"};font-weight:500;transition:all .15s}a:hover{background:${dark ? "rgba(99,102,241,0.2)" : "rgba(99,102,241,0.15)"}}</style>${result.html}` }}
              />

              {/* Matched keywords table */}
              {result.matches.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <p style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Знайдені посилання</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {result.matches.map((m, i) => (
                      <div key={i} style={{
                        display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", borderRadius: 8,
                        background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
                        fontSize: 12,
                      }}>
                        <span style={{ fontWeight: 700, color: "#6366f1", minWidth: 20 }}>#{i + 1}</span>
                        <span style={{ fontWeight: 600, minWidth: 150 }}>{m.keyword}</span>
                        <span style={{ color: t.textMuted, fontSize: 11 }}>→</span>
                        <a href={m.url} target="_blank" rel="noopener noreferrer" style={{
                          color: "#6366f1", fontSize: 11, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1,
                        }}>{m.url}</a>
                        <span style={{ color: t.textMuted, fontSize: 10, flexShrink: 0 }}>freq: {m.frequency}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
      <style>{`[data-placeholder]:empty:before{content:attr(data-placeholder);color:${t.textMuted};pointer-events:none}`}</style>
    </div>
  );
}
